import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Divider, TextField, Typography } from '@mui/material';
import { apiService } from '../services/apiService';
import {
  evaluateFunctionRaw,
  parseFunctionArgs,
  primeCustomFunctionsCache,
} from '../services/functionService';
import type { AgentMessageResponse } from '../types/agent';
import {
  buildAgentFunctionPickOptions,
  formatAgentFunctionResult,
  normalizeAgentFunctionName,
  type AgentCustomFunctionDto,
  type AgentFunctionPickOption,
} from '../utils/agentFunctionUtils';
import { AgentInlineSelect } from './ai-agent-ui';

export type AgentWidgetRunFunctionFormProps = {
  chatId?: number | null;
  defaultName?: string;
  defaultArgs?: unknown[];
  onClose: () => void;
  onUserMessage: (text: string) => void;
  onAgentReply: (res: AgentMessageResponse) => void | Promise<void>;
  onChatIdChange?: (chatId: number) => void;
  onLoadingChange?: (loading: boolean) => void;
};

const AgentWidgetRunFunctionForm: React.FC<AgentWidgetRunFunctionFormProps> = ({
  chatId,
  defaultName = '',
  defaultArgs,
  onClose,
  onUserMessage,
  onAgentReply,
  onChatIdChange,
  onLoadingChange,
}) => {
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<AgentFunctionPickOption[]>([]);
  const [fnName, setFnName] = useState('');
  const [argsText, setArgsText] = useState('');
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCustomFunctions = useCallback(async (): Promise<AgentCustomFunctionDto[]> => {
    try {
      const res = await apiService.get('/functions');
      const list = (res?.data?.functions ?? []) as AgentCustomFunctionDto[];
      primeCustomFunctionsCache(
        list.map((f) => ({
          id: f.id,
          name: f.name,
          type: f.type,
          code: f.code,
          value: f.value,
          version: f.version ?? 1,
          isActive: f.isActive !== false,
        })),
      );
      return list;
    } catch {
      return [];
    }
  }, []);

  const refreshOptions = useCallback(
    async (query: string, preferredName?: string) => {
      setLoadingOptions(true);
      try {
        const customs = await loadCustomFunctions();
        const nextOptions = buildAgentFunctionPickOptions(customs, query);
        setOptions(nextOptions);
        const preferred = preferredName ? normalizeAgentFunctionName(preferredName) : '';
        const selected =
          (preferred && nextOptions.some((o) => o.name === preferred) ? preferred : '') ||
          (fnName && nextOptions.some((o) => o.name === fnName) ? fnName : '') ||
          nextOptions[0]?.name ||
          '';
        setFnName(selected);
      } finally {
        setLoadingOptions(false);
      }
    },
    [fnName, loadCustomFunctions],
  );

  useEffect(() => {
    void (async () => {
      await refreshOptions('', defaultName);
      if (Array.isArray(defaultArgs) && defaultArgs.length > 0) {
        try {
          const serialized = defaultArgs
            .map((a) => (typeof a === 'string' ? JSON.stringify(a) : String(a)))
            .join(', ');
          setArgsText(serialized);
        } catch {
          setArgsText('');
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount + defaultName only
  }, [defaultName]);

  const handleSearchChange = (next: string) => {
    setSearch(next);
    void refreshOptions(next);
  };

  const handleRun = async () => {
    setError(null);
    const selected = normalizeAgentFunctionName(fnName);
    if (!selected) {
      setError('Select a function to run.');
      return;
    }

    let args: unknown[] = [];
    try {
      args = parseFunctionArgs(argsText);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not parse arguments.');
      return;
    }

    const argsPreview =
      args.length === 0
        ? '()'
        : `(${args.map((a) => (typeof a === 'string' ? JSON.stringify(a) : String(a))).join(', ')})`;

    let result: unknown;
    let success = true;
    let evalError: string | undefined;
    try {
      result = await evaluateFunctionRaw(selected, ...args);
    } catch (e: unknown) {
      success = false;
      evalError = e instanceof Error ? e.message : 'Function execution failed.';
      result = evalError;
    }

    const userText = `Run function ${selected}${argsPreview}.`;
    onUserMessage(userText);
    setSubmitting(true);
    onLoadingChange?.(true);
    try {
      const res = await apiService.agentMessage({
        action: 'run_function',
        chatId: chatId ?? undefined,
        payload: {
          name: selected,
          args,
          result: success ? result : undefined,
          clientEvaluated: true,
          success,
          error: evalError,
          ...(success ? {} : { result: formatAgentFunctionResult(result) }),
        },
      });
      if (res.state?.chatId != null) {
        onChatIdChange?.(res.state.chatId as number);
      }
      if (success) {
        onClose();
      } else {
        setError(evalError || 'Function execution failed.');
      }
      await onAgentReply(res);
    } finally {
      setSubmitting(false);
      onLoadingChange?.(false);
    }
  };

  const busy = submitting || loadingOptions;

  return (
    <Box
      sx={{
        mt: 1,
        mb: 0.5,
        p: 1.25,
        borderRadius: 2,
        bgcolor: 'rgba(255,255,255,0.95)',
        border: '1px solid rgba(15,23,42,0.12)',
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
        Run function
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
        <TextField
          id="widget-run-fn-search"
          label="Search"
          size="small"
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Filter by name or category"
          fullWidth
          disabled={submitting}
        />
        <AgentInlineSelect
          id="widget-run-fn-name"
          label="Function"
          fullWidth
          value={fnName}
          disabled={busy}
          emptyLabel={loadingOptions ? 'Loading functions…' : 'No functions found'}
          maxListHeight={280}
          options={options.map((o) => ({
            value: o.name,
            label: o.label,
          }))}
          onChange={(raw) => setFnName(raw)}
        />
        <TextField
          id="widget-run-fn-args"
          label="Arguments"
          size="small"
          value={argsText}
          onChange={(e) => setArgsText(e.target.value)}
          placeholder='"hello", 42'
          fullWidth
          disabled={busy}
        />
      </Box>

      {error && (
        <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 1 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button variant="text" size="small" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={() => void handleRun()}
          disabled={busy || !fnName}
        >
          Run
        </Button>
      </Box>

      <Divider sx={{ mt: 1 }} />
    </Box>
  );
};

export default AgentWidgetRunFunctionForm;
